import React, { useState } from 'react';
import { Inbox, Mail, CheckCircle, XCircle, Clock, Search, RefreshCw, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';

// Components
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

// Hooks
import { useEmailTracker } from '../../hooks/email/useEmailTracker';

// Types
import { EmailRecord } from '../../services/email/emailTrackerService';

function EmailTrackerTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [emailTypeFilter, setEmailTypeFilter] = useState<string>('all');
  
  const {
    emails,
    loading,
    error,
    total,
    count,
    offset,
    refreshData,
    loadMore,
    loadPrevious,
    resetPagination
  } = useEmailTracker();

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Mail className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'sent':
        return <Badge variant="default" className="bg-green-100 text-green-800">Sent</Badge>;
      case 'failed':
        return <Badge variant="destructive" className="text-white">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateString);
        return 'Invalid Date';
      }
      
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Invalid Date';
    }
  };

  const filteredEmails = emails.filter(email => {
    const matchesSearch = email.recipient.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         email.event_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || email.status === statusFilter;
    const matchesEmailType = emailTypeFilter === 'all' || email.email_type === emailTypeFilter;
    return matchesSearch && matchesStatus && matchesEmailType;
  });

  // Show loading skeletons when refreshing existing data
  const showLoadingSkeletons = loading && emails.length > 0;

  if (loading && emails.length === 0) {
    return (
      <div className="space-y-6">
        {/* Filters and Refresh Skeleton */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          <div className="flex flex-col md:flex-row gap-4 flex-1">
            <div className="relative flex-1">
              <div className="h-10 bg-muted rounded animate-pulse" />
            </div>
            <div className="w-full md:w-48">
              <div className="h-10 bg-muted rounded animate-pulse" />
            </div>
          </div>
          <div className="w-full md:w-auto">
            <div className="h-10 bg-muted rounded animate-pulse" />
          </div>
        </div>

        {/* Mobile Email List Skeleton */}
        <div className="space-y-4 md:hidden">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
            <Card key={i} className="border-l-4 border-l-gray-300">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="h-5 bg-muted rounded animate-pulse mb-2" />
                      <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="h-6 w-20 bg-muted rounded animate-pulse" />
                    <div className="h-6 w-16 bg-muted rounded animate-pulse" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-muted rounded animate-pulse w-32" />
                    <div className="h-4 bg-muted rounded animate-pulse w-40" />
                    <div className="h-4 bg-muted rounded animate-pulse w-36" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Desktop Email List Skeleton */}
        <div className="hidden md:block">
          <div className="border rounded-lg overflow-hidden w-full">
            <div className="min-h-[500px] max-h-[500px] overflow-auto w-full bg-gray-50">
              {/* Bounce Notice Skeleton */}
              <div className="p-4 bg-blue-50 border-b border-blue-200">
                <div className="h-4 bg-muted rounded animate-pulse w-full" />
              </div>
              <div>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                  <Card key={i} className="rounded-none border-b border-gray-200 border-l-4 border-l-gray-300">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="h-5 bg-muted rounded animate-pulse mb-2" />
                          <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <div className="h-6 w-20 bg-muted rounded animate-pulse" />
                          <div className="h-6 w-16 bg-muted rounded animate-pulse" />
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <div className="h-4 bg-muted rounded animate-pulse w-32 inline-block" />
                        <div className="h-4 bg-muted rounded animate-pulse w-40 inline-block ml-4" />
                        <div className="h-4 bg-muted rounded animate-pulse w-36 inline-block ml-4" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <XCircle className="h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-center">Error Loading Emails</h3>
            <p className="text-muted-foreground text-center text-sm mb-4">
              {error}
            </p>
            <Button onClick={refreshData} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters and Refresh */}
      <div className="flex flex-col md:flex-row md:justify-between gap-4">
        <div className="flex flex-col md:flex-row gap-4 flex-1">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by recipient, subject, or event..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10"
            />
          </div>
          <div className="w-full md:w-48">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-48">
            <Select value={emailTypeFilter} onValueChange={setEmailTypeFilter}>
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Filter by email type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Email Types</SelectItem>
                <SelectItem value="Ticket Email">Ticket Email</SelectItem>
                <SelectItem value="Email Blast">Email Blast</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={refreshData} disabled={loading} className="h-10">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between">
        <div className="hidden md:block text-sm text-muted-foreground">
          {total === 0 ? 'No emails found' : `Showing ${offset + 1} to ${Math.min(offset + count, total)} of ${total} emails`}
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={loadPrevious}
            disabled={offset === 0 || loading}
            className="flex-1 md:flex-none"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadMore}
            disabled={loading || offset + count >= total}
            className="flex-1 md:flex-none"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Mobile Email List */}
      <div className="md:hidden space-y-4">
        {/* Bounce Notice */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Emails may initially appear as "Sent" but can bounce back later and show as "Failed". 
            Use the refresh button to check for updated delivery status. If an attendee's ticket email shows up as "Failed", try using the reissue feature on the event's attendees page to resend the email.
          </p>
        </div>
        
        {showLoadingSkeletons ? (
          // Show skeleton loading when refreshing
          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <Card key={i} className="border-l-4 border-l-gray-300">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-start gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="h-5 bg-muted rounded animate-pulse mb-2" />
                        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="h-6 w-20 bg-muted rounded animate-pulse" />
                      <div className="h-6 w-16 bg-muted rounded animate-pulse" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-4 bg-muted rounded animate-pulse w-32" />
                      <div className="h-4 bg-muted rounded animate-pulse w-40" />
                      <div className="h-4 bg-muted rounded animate-pulse w-36" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredEmails.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Inbox className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-center">No email records found</h3>
              <p className="text-muted-foreground text-center text-sm">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Emails will appear here once emails are sent.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredEmails.map((email) => (
              <Card key={email.id} className={`${email.status === 'sent' ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'}`}>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-start gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate text-base">{email.subject}</p>
                        <p className="text-sm text-muted-foreground truncate mt-1">{email.recipient}</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <Badge variant="outline" className="text-xs px-2 py-1 rounded-md">
                        {email.email_type}
                      </Badge>
                      {getStatusBadge(email.status)}
                    </div>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p><strong>Sent:</strong> {formatDate(email.sent_at)}</p>
                      {email.delivered_at && (
                        <p><strong>Delivered:</strong> {formatDate(email.delivered_at)}</p>
                      )}
                      {email.opened_at && (
                        <p><strong>Opened:</strong> {formatDate(email.opened_at)}</p>
                      )}
                      {email.bounce_details && (
                        <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                          <strong>Bounce Reason:</strong> {email.bounce_details.Details || 'Unknown'}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        

      </div>

      {/* Desktop Email List */}
      <div className="hidden md:block">
        <div className="border rounded-lg overflow-hidden w-full">
          <div className="min-h-[500px] max-h-[500px] overflow-auto w-full bg-gray-50">
            {/* Bounce Notice */}
            <div className="p-4 bg-blue-50 border-b border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Emails may initially appear as "Sent" but can bounce back later and show as "Failed". 
                Use the refresh button to check for updated delivery status. If an attendee's ticket email shows up as "Failed", try using the reissue feature on the event's attendees page to resend the email.
              </p>
            </div>
            <div>
              {showLoadingSkeletons ? (
                // Show skeleton loading when refreshing
                <div>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                  <Card key={i} className="rounded-none border-b border-gray-200 border-l-4 border-l-gray-300">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="h-5 bg-muted rounded animate-pulse mb-2" />
                          <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <div className="h-6 w-20 bg-muted rounded animate-pulse" />
                          <div className="h-6 w-16 bg-muted rounded animate-pulse" />
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <div className="h-4 bg-muted rounded animate-pulse w-32 inline-block" />
                        <div className="h-4 bg-muted rounded animate-pulse w-40 inline-block ml-4" />
                        <div className="h-4 bg-muted rounded animate-pulse w-36 inline-block ml-4" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredEmails.length === 0 ? (
              <Card className="rounded-none">
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <Inbox className="h-10 w-10 md:h-12 md:w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2 text-center">No email records found</h3>
                  <p className="text-muted-foreground text-center mb-4 text-sm md:text-base">
                    {searchQuery || statusFilter !== 'all' 
                      ? 'Try adjusting your search or filter criteria.'
                      : 'Emails will appear here once emails are sent.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {filteredEmails.map((email) => (
                  <Card key={email.id} className={`rounded-none border-b border-gray-200 ${email.status === 'sent' ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 mb-1">{email.subject}</h3>
                          <p className="text-sm text-gray-600"><strong>To:</strong> {email.recipient}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Badge variant="outline" className="text-xs px-2 py-1 rounded-md">
                            {email.email_type}
                          </Badge>
                          {getStatusBadge(email.status)}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <span><strong>Sent:</strong> {formatDate(email.sent_at)}</span>
                        {email.delivered_at && (
                          <span className="ml-4"><strong>Delivered:</strong> {formatDate(email.delivered_at)}</span>
                        )}
                        {email.opened_at && (
                          <span className="ml-4"><strong>Opened:</strong> {formatDate(email.opened_at)}</span>
                        )}
                      </div>
                      {email.bounce_details && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                          <strong>Bounce Reason:</strong> {email.bounce_details.Details || 'Unknown'}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
                

              </>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailTrackerTab; 